"use client";

import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import Script from "next/script";
import {RecaptchaAction, RecaptchaVersion} from "@/utils/google/utils";

declare global {
    const grecaptcha: {
        enterprise: {
            ready: (cb: () => void) => void;
            execute: (
                siteKey: string,
                options: { action: string }
            ) => Promise<string>;
        };
    };
}

type RecaptchaState = {
    captcha: string | null,
    captchaVersion: RecaptchaVersion;
}

interface ReCaptchaContextProps {
    readonly loaded: boolean;
    readonly isReCaptchaVisible: boolean;
    readonly reCaptchaState: RecaptchaState;
    executeReCaptcha: (action: RecaptchaAction) => Promise<string | null>;
    renderReCaptcha: (containerId: string, action?: string) => Promise<void>;
    refreshReCaptcha: (action?: RecaptchaAction) => Promise<string | null>;
    resetReCaptcha: (action: RecaptchaAction) => Promise<string | null>;
}

const ReCaptchaContext = createContext<ReCaptchaContextProps>({
    loaded: false,
    isReCaptchaVisible: false,
    reCaptchaState: {
        captcha: null,
        captchaVersion: RecaptchaVersion.V3,
    },
    executeReCaptcha: async () => null,
    renderReCaptcha: async () => undefined,
    refreshReCaptcha: async () => null,
    resetReCaptcha: async () => null,
});

export const ReCaptchaProvider = ({ children } : { children: React.ReactNode }) => {

    const [loaded, setLoaded] = useState<boolean>(false);
    const [version, setVersion] = useState<RecaptchaVersion>(RecaptchaVersion.V3);
    const [captchaV2, setCaptchaV2] = useState<string | null>(null);
    const [captchaV3, setCaptchaV3] = useState<string | null>(null);
    const [reCaptchaState, setReCaptchaState] = useState<RecaptchaState>({
        captcha: null,
        captchaVersion: RecaptchaVersion.V3,
    });

    const [renderedReCaptchaV2, setRenderedReCaptchaV2] = useState<any>(null);
    const [isReCaptchaVisible, setIsReCaptchaVisible] = useState<boolean>(false);

    /**
     * Initial load
     */
    const onLoad = useCallback(() => {
        const grecaptcha = window?.grecaptcha?.enterprise;
        if (grecaptcha && typeof grecaptcha.ready === "function") {
            grecaptcha.ready(() => {
                setLoaded(true);
            });
        }
    }, []);

    /**
     * Execute ReCaptcha V3
     */
    const executeReCaptcha = useCallback(async (action: RecaptchaAction): Promise<string | null> => {
        try {
            if (!loaded || !window?.grecaptcha?.enterprise) {
                console.error("ReCaptcha not loaded");
                return null;
            }
            const result = await window.grecaptcha.enterprise.execute(
                process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY_V3!,
                { action }
            );
            setCaptchaV3(result);
            return result;
        } catch (error) {
            console.error("Failed to generate ReCaptcha V3", error);
            return null;
        }
    }, [loaded]);

    /**
     * Render ReCaptcha V2
     */
    const renderReCaptcha = useCallback(async (containerId: string, action?: string): Promise<void> => {
        try {
            if (!loaded || !window?.grecaptcha?.enterprise) {
                console.error("Error rendering ReCaptcha V2: ReCaptcha not fully loaded.");
                return;
            }
            setVersion(RecaptchaVersion.V2);
            setIsReCaptchaVisible(true);
            if (renderedReCaptchaV2 !== null) {
                window.grecaptcha.enterprise.reset(renderedReCaptchaV2);
            } else {
                const widgetId = window.grecaptcha.enterprise.render(containerId, {
                    sitekey: process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY_V2!,
                    action: action || "login",
                    callback: (token: string) => {
                        setCaptchaV2(token);
                    }
                });
                setRenderedReCaptchaV2(widgetId);
            }
        } catch (error) {
            console.error("Error rendering ReCaptcha V2:", error);
        }
    }, [loaded, renderedReCaptchaV2]);

    /**
     * Refresh ReCaptcha (V2 and V3)
     */
    const refreshReCaptcha = useCallback(async (action?: RecaptchaAction): Promise<string | null> => {
        if(version === RecaptchaVersion.V3 && action) {
            return await executeReCaptcha(action);
        } else if(version === RecaptchaVersion.V2) {
            await window.grecaptcha.enterprise.reset(renderedReCaptchaV2);
        }
        return null;
    }, [executeReCaptcha, renderedReCaptchaV2]);

    /**
     * Reset and refresh ReCaptcha back to V3
     */
    const resetReCaptcha = useCallback(async (action: RecaptchaAction): Promise<string | null> => {
        try {
            setVersion(RecaptchaVersion.V3);
            setIsReCaptchaVisible(false);
            return await executeReCaptcha(action);
        } catch (error) {
            console.error("Failed to reset and regenerate ReCaptcha V3", error);
            return null;
        }
    }, [executeReCaptcha]);

    /**
     * Handle states
     */
    useEffect(() => {
        if (window?.grecaptcha) {
            onLoad();
        }
    }, [onLoad]);

    useEffect(() => {
        if(loaded) {
            setReCaptchaState({
                captcha: (version === RecaptchaVersion.V3) ? captchaV3 : captchaV2,
                captchaVersion: version,
            });
        }
    }, [loaded, version, captchaV2, captchaV3]);

    useEffect(() => {
        setIsReCaptchaVisible(
            Boolean(version === RecaptchaVersion.V2)
        )
    }, [version]);

    /**
     * Provider values
     */
    const value = useMemo(() => {
        return {
            loaded,
            isReCaptchaVisible: isReCaptchaVisible,
            reCaptchaState: reCaptchaState,
            executeReCaptcha,
            renderReCaptcha,
            refreshReCaptcha,
            resetReCaptcha,
        };
    }, [loaded, version, reCaptchaState, executeReCaptcha, renderReCaptcha, refreshReCaptcha, resetReCaptcha]);

    return (
        <ReCaptchaContext.Provider value={value}>
            {children}
            <Script
                src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY_V3}`}
                strategy="afterInteractive"
                onLoad={onLoad} />
        </ReCaptchaContext.Provider>
    );

}

export const useReCaptcha = () => {
    const context = useContext(ReCaptchaContext);
    if (context === undefined) {
        throw new Error("useReCaptcha must be used within an ReCaptchaProvider.");
    }
    return context;
};

export function WithReCaptcha<T extends object>(Component: React.ComponentType<T>): React.FC<T> {
    return function WrappedComponent(props: T) {
        return (
            <ReCaptchaProvider>
                <Component {...props} />
            </ReCaptchaProvider>
        );
    };
}